import {
  createContext,
  use,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { Accessibility, KeyboardSensor } from "@dnd-kit/dom";
import { pointerIntersection } from "@dnd-kit/collision";
import type { CollisionDetector } from "@dnd-kit/collision";
import { CollisionPriority } from "@dnd-kit/abstract";
import {
  DragDropProvider,
  DragOverlay,
  useDraggable,
  useDroppable,
} from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { move } from "@dnd-kit/helpers";
import { DragPreview } from "./DragHandle";

/**
 * Every list a screen lets something be dragged within or between, each
 * holding the ids of its rows in order, addressed by the list's own name.
 */
export type Lanes = Record<string, string[]>;

/** The part of a drag event the wording needs, named rather than inferred. */
export interface DragEventShape {
  operation: {
    source?: { id: unknown } | null;
    target?: { id: unknown } | null;
  };
  /** Only `dragend` carries this, and only it says the drag was abandoned. */
  canceled?: boolean;
}

/**
 * What the live region says during a drag.
 *
 * The library builds the region and speaks through it without configuration,
 * but its default wording interpolates the item's identifier, and ours are
 * database ids. Read aloud, an id is the one thing on the row that identifies
 * nothing, so the wording is replaced with what the row already shows.
 *
 * `name` is asked when something is announced rather than when this is built,
 * because the plugin holding it is constructed once and the rows arrive later,
 * with the query.
 */
export function dragAnnouncements(name: (id: unknown) => string) {
  // A row reports itself as its own drop target on pick-up, and again on a drop
  // that moved nothing. Announcing that reads as "X is over X", which describes
  // nothing and crowds out the announcement that does.
  const elsewhere = ({ operation }: DragEventShape) =>
    operation.target === null || operation.target === undefined
      ? undefined
      : operation.target.id === operation.source?.id
        ? undefined
        : name(operation.target.id);

  return {
    dragstart: ({ operation }: DragEventShape) =>
      `Picked up ${name(operation.source?.id)}.`,
    dragover: (event: DragEventShape) => {
      const over = elsewhere(event);
      return over === undefined
        ? undefined
        : `${name(event.operation.source?.id)} is over ${over}.`;
    },
    dragend: (event: DragEventShape) => {
      const row = name(event.operation.source?.id);
      // Whether the drag was abandoned is its own flag, not something the drop
      // target says: a sort that completed reports the row as its own target,
      // because by then it is sitting where it landed.
      if (event.canceled === true) {
        return `${row} was put back where it started.`;
      }
      const onto = elsewhere(event);
      return onto === undefined
        ? `${row} was dropped into place.`
        : `${row} was dropped on ${onto}.`;
    },
  };
}

/**
 * The arrangement as the screen draws it, which during a drag runs ahead of
 * the server's.
 *
 * The library moves the rows itself as the pointer passes them, and this
 * follows it move for move, so the tree React renders and the tree in the
 * document never disagree. Reading only the server's arrangement and writing on
 * drop leaves them to diverge for the length of the drag, which is survivable
 * while a row stays in one list and is not once it crosses between two: React
 * is then asked to remove a row from a list the library has already taken it
 * out of.
 */
export function useFollowedLanes(
  served: Lanes,
  onArrange: (lanes: Lanes, putBack: () => void, moved?: string) => void,
) {
  const servedKey = useMemo(() => JSON.stringify(served), [served]);
  const [lanes, setLanes] = useState(served);
  const [shown, setShown] = useState(servedKey);
  // What a release would land on, taken from the drag itself rather than from
  // the library's own flag: that flag never re-rendered anything.
  const [over, setOver] = useState<string | null>(null);
  // Whether a drag is in flight, which decides whether a fresh arrangement from
  // the server may be adopted. A refetch this screen did not ask for can land
  // mid-drag, and adopting it would replace the rows under the library while it
  // is moving them, which is the divergence that blanked the figures screen.
  const draggingRef = useRef(false);
  if (shown !== servedKey && !draggingRef.current) {
    setShown(servedKey);
    setLanes(served);
  }

  const lanesRef = useRef(lanes);
  lanesRef.current = lanes;
  // The server's arrangement as it stands when a refusal arrives, which is
  // later than the drop and may be later than another drag.
  const servedRef = useRef(served);
  servedRef.current = served;
  // The arrangement as it stood when the drag began, kept for an abandoned one.
  const beforeDragRef = useRef(lanes);

  const handlers = {
    onDragStart: () => {
      beforeDragRef.current = lanesRef.current;
      draggingRef.current = true;
      setOver(null);
    },
    /** Where the drag is going, taken as it happens rather than on drop. */
    onDragOver: (target: string | null, step: (current: Lanes) => Lanes) => {
      setOver(target);
      const next = step(lanesRef.current);
      lanesRef.current = next;
      setLanes(next);
    },
    /** `moved` is the row the drag carried, so a screen can move that one row against newer data. */
    onDragEnd: (canceled: boolean, moved?: string) => {
      setOver(null);
      draggingRef.current = false;
      // An abandoned drag puts back the arrangement the drag started from. The
      // library reverses its own movements, and the ones made here while
      // following it have to go back with them.
      if (canceled) {
        lanesRef.current = beforeDragRef.current;
        setLanes(beforeDragRef.current);
        return;
      }
      // A refused write leaves the server holding the old arrangement, and
      // nothing else puts the rows back: the resync above only fires on a
      // served arrangement that changed, and a refusal changes none. The rows
      // would keep the order the drag gave them while the numbers on them keep
      // the server's, so the set reads 1, 3, 2 numbered 1, 2, 3.
      const putBack = () => {
        lanesRef.current = servedRef.current;
        setLanes(servedRef.current);
        setShown(JSON.stringify(servedRef.current));
      };
      // The arrangement is already the one the drag arrived at, because every
      // move was applied as it happened. Applying the same operation again here
      // would move the row a second time: asked to place it after a sibling,
      // the first pass puts it there and the second reads it as sitting one
      // place too far and sends it back in front.
      const after = lanesRef.current;
      if (JSON.stringify(after) !== servedKey) onArrange(after, putBack, moved);
    },
  };

  /**
   * Move one row a place up or down its lane, written at once: the keyboard's
   * move, which needs no layout and so serves a row the drag cannot reach.
   */
  const step = (id: string, by: -1 | 1) => {
    const now = lanesRef.current;
    const lane = Object.keys(now).find((key) => now[key].includes(id));
    if (lane === undefined || draggingRef.current) return;
    const rows = [...now[lane]];
    const from = rows.indexOf(id);
    const to = from + by;
    if (to < 0 || to >= rows.length) return;
    rows.splice(from, 1);
    rows.splice(to, 0, id);
    const next = { ...now, [lane]: rows };
    lanesRef.current = next;
    setLanes(next);
    onArrange(
      next,
      () => {
        lanesRef.current = servedRef.current;
        setLanes(servedRef.current);
        setShown(JSON.stringify(servedRef.current));
      },
      id,
    );
  };

  return { lanes, over, handlers, step };
}

/** The keyboard's move inside the nearest `SortableLanes`, or nothing outside one. */
const StepContext = createContext<(id: string, by: -1 | 1) => void>(
  () => undefined,
);

/**
 * What a reader is told about a row's handle. The library's own keyboard drag is
 * off: it steps by the layout it measures and breaks across nested lists (a
 * figure stepped past a group lands inside it), so the Alt keys move a row.
 */
export const HANDLE_INSTRUCTIONS =
  "To move this item one place, press Alt and the up or down arrow.";

/** The keys that move a row one place. */
const STEP_KEYS: Partial<Record<string, -1 | 1>> = {
  ArrowUp: -1,
  ArrowDown: 1,
};

/** A drop that lands somewhere rather than sorting: what was dropped, and on what. */
export interface Landing {
  source: string;
  target: string;
}

/**
 * Where an item of a kind that lands was dropped, or nothing: for a put-back,
 * for a drop on nothing, and for a kind that sorts, whose drop is the lanes'.
 */
export function landingOf(
  event: {
    operation: {
      source?: { id: unknown; type?: unknown } | null;
      target?: { id: unknown } | null;
    };
    canceled?: boolean;
  },
  landingTypes: readonly string[],
): Landing | null {
  const { source, target } = event.operation;
  if (event.canceled === true || source == null || target == null) return null;
  if (!landingTypes.includes(String(source.type))) return null;
  return { source: String(source.id), target: String(target.id) };
}

/** What a screen inside the drag is given to draw from. */
export interface SortableLanesView {
  lanes: Lanes;
  /** What a release right now would land on, anywhere on the screen. */
  over: string | null;
}

/**
 * The one drag mechanism: lists of rows that reorder by their handles, within
 * a list and between lists, written once on the drop.
 *
 * A screen supplies the arrangement the server serves, how a reader hears each
 * id, and what a finished rearrangement writes. What may land where is each
 * row's and each lane's own rule, passed to `useSortableRow` and `useLane`.
 */
export function SortableLanes({
  served,
  name,
  onArrange,
  lands = [],
  onLand,
  carried,
  children,
}: {
  served: Lanes;
  /** How a reader hears an id: a row's own words, or where a lane puts what lands in it. */
  name: (id: unknown) => string;
  /** What a finished rearrangement writes, how it puts the rows back if refused, and which row moved. */
  onArrange: (lanes: Lanes, putBack: () => void, moved?: string) => void;
  /** Kinds of item dragged onto a target rather than sorted into a lane. */
  lands?: readonly string[];
  /** What a drop of one of those kinds does. */
  onLand?: (landing: Landing) => void;
  /** The words an item that lands shows under the pointer while it is carried. */
  carried?: (id: string) => ReactNode;
  children: (view: SortableLanesView) => ReactNode;
}) {
  const { lanes, over, handlers, step } = useFollowedLanes(served, onArrange);
  // The plugin below is built once, before the rows have arrived, so what it
  // holds has to be able to see them later.
  const nameRef = useRef(name);
  nameRef.current = name;

  return (
    <StepContext value={step}>
      <DragDropProvider
        // The preset is kept and only its wording replaced, so the live region,
        // the keyboard sensor and the rest arrive as the library intends them.
        plugins={(defaults) =>
          defaults.map((plugin) =>
            plugin === Accessibility
              ? Accessibility.configure({
                  announcements: dragAnnouncements((id) => nameRef.current(id)),
                  screenReaderInstructions: { draggable: HANDLE_INSTRUCTIONS },
                })
              : plugin,
          )
        }
        // The pointer drags; the keyboard moves a row through the Alt keys instead.
        sensors={(defaults) =>
          defaults.filter((sensor) => sensor !== KeyboardSensor)
        }
        onDragStart={handlers.onDragStart}
        onDragOver={(event) => {
          const target: unknown = event.operation.target?.id;
          // An item that lands never enters a lane, so the lanes stay as they are.
          const landing = lands.includes(String(event.operation.source?.type));
          handlers.onDragOver(
            typeof target === "string" ? target : null,
            (current) => (landing ? current : move(current, event)),
          );
        }}
        onDragEnd={(event) => {
          const landing = landingOf(event, lands);
          const source: unknown = event.operation.source?.id;
          handlers.onDragEnd(
            event.canceled,
            typeof source === "string" || typeof source === "number"
              ? String(source)
              : undefined,
          );
          if (landing !== null) onLand?.(landing);
        }}
      >
        {children({ lanes, over })}
        {carried !== undefined && (
          // Only an item that lands is carried as a preview; a sorted row moves itself.
          <DragOverlay
            className="pointer-events-none"
            disabled={(source) => !lands.includes(String(source?.type))}
          >
            {(source) => (
              <DragPreview>{carried(String(source.id))}</DragPreview>
            )}
          </DragOverlay>
        )}
      </DragDropProvider>
    </StepContext>
  );
}

/**
 * A row sorted within the lane it currently sits in.
 *
 * The lane is passed as the library's `group`. Left unnamed, a row sorts in a
 * list of its own, against a different set of rows from the ones beside it.
 */
export function useSortableRow({
  id,
  index,
  lane,
  type,
  accept,
  collisionDetector,
}: {
  id: string;
  index: number;
  lane: string;
  /** What kind of row this is, which a lane's or a row's `accept` names. */
  type: string;
  /** What may be dropped beside this row. */
  accept?: string | string[];
  collisionDetector?: CollisionDetector;
}) {
  const sortable = useSortable({
    id,
    index,
    group: lane,
    type,
    accept,
    collisionDetector,
  });
  const step = use(StepContext);
  const stepRef = useRef({ step, id });
  stepRef.current = { step, id };
  const dragHandleRef = sortable.handleRef;
  const detachRef = useRef<(() => void) | null>(null);
  // Alt with an arrow moves the row a place, from its handle: the drag's own keys move
  // only through a layout the library measures, which a row out of view does not have.
  // The handle keeps the focus and is brought into view, so the next press moves it again.
  const handleRef = useCallback(
    (element: Element | null) => {
      dragHandleRef(element);
      detachRef.current?.();
      detachRef.current = null;
      if (!(element instanceof HTMLElement)) return;
      element.setAttribute("aria-keyshortcuts", "Alt+ArrowUp Alt+ArrowDown");
      const onKey = (event: KeyboardEvent) => {
        const by = STEP_KEYS[event.key];
        if (!event.altKey || by === undefined) return;
        event.preventDefault();
        stepRef.current.step(stepRef.current.id, by);
        requestAnimationFrame(() => {
          element.focus();
          element.scrollIntoView({ block: "nearest" });
        });
      };
      element.addEventListener("keydown", onKey);
      detachRef.current = () => {
        element.removeEventListener("keydown", onKey);
      };
    },
    [dragHandleRef],
  );
  return { ...sortable, handleRef };
}

/** Somewhere a row can be dropped that is not itself a row: an empty list, or the inside of a group. */
export function useLane({
  id,
  accept,
  collisionPriority,
  collisionDetector,
}: {
  id: string;
  accept: string | string[];
  collisionPriority?: CollisionPriority;
  collisionDetector?: CollisionDetector;
}) {
  return useDroppable({
    id,
    type: "lane",
    accept,
    collisionPriority,
    collisionDetector,
  });
}

/** An item carried onto a target rather than sorted: a position dropped on the claim it should join. */
export function useDragItem({ id, type }: { id: string; type: string }) {
  return useDraggable({ id, type });
}

/**
 * A pointer hit worth more the deeper the target sits, so a target inside
 * another takes the drop.
 *
 * Priority alone cannot say it: targets that share a priority are ranked by how
 * near the pointer is to the middle of each, and the middle of a card is often
 * nearer than the middle of the row the pointer is actually over. A hit is worth
 * at most 1, so one whole point a level puts every deeper target ahead.
 */
export function deeperFirst(depth: number): CollisionDetector {
  return (input) => {
    const collision = pointerIntersection(input);
    return collision === null
      ? null
      : { ...collision, value: collision.value + depth };
  };
}

/** Something an item that lands can be dropped on. */
export function useDropTarget({
  id,
  accept,
  inner = false,
  depth = 0,
}: {
  id: string;
  accept: string | string[];
  /** A target inside another, which takes the drop when the pointer is over both. */
  inner?: boolean;
  /** How deep the target sits inside other targets; the deepest one under the pointer wins. */
  depth?: number;
}) {
  // Aimed by the pointer rather than by overlap: a carried row is wide, and
  // by overlap it lands on whichever card it covers most, not the one pointed at.
  // Every pointer hit is already high priority, so an inner target must be highest.
  return useDroppable({
    id,
    type: "target",
    accept,
    collisionDetector: deeperFirst(depth),
    collisionPriority: inner ? CollisionPriority.Highest : undefined,
  });
}
